import moment from "moment";
import i18n from "locales/i18n";
import { ParseKeys } from "i18next";
import { BookingType } from "../calendar/eventTypes";
import { AttendeeStatus, Booking } from "../calendar/booking/types";

// Booking-level status persisted in Phase 2. "completed" is NOT stored — it is
// derived from a confirmed meeting whose end time has already passed.
export type MeetingStatus = "pending" | "confirmed" | "cancelled";
// Display status blends the vendor's booking status with the customer's invite
// response: a declined / reschedule-proposed response surfaces as its own status.
export type DisplayStatus =
  | "pending"
  | "awaiting_customer"
  | "confirmed"
  | "completed"
  | "cancelled"
  | "declined"
  | "reschedule_requested";

export interface MeetingStatusMeta {
  label: string;
  color: string; // hex, for chips
}

const tm = i18n.getFixedT(null, "meetings");

// `label` is read at render time, so it follows the language switcher.
const labelled = (key: ParseKeys<"meetings">, color: string) => ({
  get label() {
    return tm(key);
  },
  color,
});

export const MEETING_STATUS_META: Record<DisplayStatus, MeetingStatusMeta> = {
  pending: labelled("status.pending", "#f59e0b"),
  awaiting_customer: labelled("status.awaiting_customer", "#6366f1"),
  confirmed: labelled("status.confirmed", "#22c55e"),
  completed: labelled("status.completed", "#3b82f6"),
  cancelled: labelled("status.cancelled", "#9ca3af"),
  declined: labelled("status.declined", "#ef4444"),
  reschedule_requested: labelled("status.reschedule_requested", "#f97316"),
};

/** A status filter's label, including the catch-all "All Meetings". */
export const statusFilterLabel = (key: DisplayStatus | "all") =>
  key === "all" ? tm("status.all") : MEETING_STATUS_META[key].label;

// The estimate/job a meeting relates to. Inferred from the customer's Leads in
// Phase 1; becomes an explicit relation (quoteId) in Phase 2.
export interface MeetingEstimate {
  quoteId: number;
  estimateNumber?: string;
  chargerModel?: string;
  totalCost?: number | null;
  stage?: string;
}

export interface Meeting {
  id: string;
  booking: Booking; // underlying booking (attendees, attachments, etc.)
  title: string;
  type: BookingType;
  date: string; // "YYYY-MM-DD"
  from: string; // "HH:mm"
  to: string; // "HH:mm"
  allDay?: boolean;
  customerName: string;
  customerEmail?: string;
  customerPhone?: string;
  status: MeetingStatus;
  displayStatus: DisplayStatus;
  meetingLink?: string;
  vendorNote?: string;
  estimate?: MeetingEstimate;
  // The invited customer's response (attendee-level), surfaced to the vendor.
  customerResponse?: AttendeeStatus;
  proposedDate?: string;
  proposedFrom?: string;
  proposedTo?: string;
  createdAt?: string;
}

// How the customer's invite response renders for the vendor. "invited" (awaiting)
// is intentionally absent — only actioned responses are highlighted.
export const CUSTOMER_RESPONSE_META: Partial<
  Record<AttendeeStatus, { label: string; color: string }>
> = {
  confirmed: labelled("response.confirmed", "#22c55e"),
  declined: labelled("response.declined", "#ef4444"),
  reschedule_proposed: labelled("response.reschedule_proposed", "#f59e0b"),
};

// Only video meetings require vendor confirmation (a unique meeting link); all
// other types are confirmed on creation.
export const defaultStatus = (type: BookingType): MeetingStatus =>
  type === "video" ? "pending" : "confirmed";

export const deriveDisplayStatus = (
  status: MeetingStatus,
  date: string,
  to: string,
  customerResponse?: AttendeeStatus,
  hasCustomer = false,
): DisplayStatus => {
  // A vendor cancellation trumps everything.
  if (status === "cancelled") {
    return "cancelled";
  }
  // Then the customer's invite response, if they declined or proposed a new time.
  if (customerResponse === "declined") {
    return "declined";
  }
  if (customerResponse === "reschedule_proposed") {
    return "reschedule_requested";
  }
  // The vendor still has to confirm (e.g. a video meeting awaiting a link).
  if (status === "pending") {
    return "pending";
  }
  const end = moment(`${date} ${to}`, "YYYY-MM-DD HH:mm");
  if (end.isValid() && end.isBefore(moment())) {
    return "completed";
  }
  // Vendor confirmed + invite sent, but the customer hasn't accepted yet — don't
  // mislead the vendor into thinking it's mutually confirmed.
  if (hasCustomer && customerResponse !== "confirmed") {
    return "awaiting_customer";
  }
  return "confirmed";
};

// Meeting types shown in the dashboard — "blocked" is an availability block,
// not a meeting, so it is excluded.
export const MEETING_TYPES: BookingType[] = [
  "video",
  "call",
  "onsite",
  "inspection",
];

export type StatusFilter = DisplayStatus | "all";
export type TypeFilter = BookingType | "all";
export type DateFilter =
  | "all"
  | "upcoming"
  | "past"
  | "today"
  | "week"
  | "month";

// Count per status bucket (plus "all"). Keyed by every StatusFilter so the
// sidebar and stat cards can show declined / reschedule counts too.
export type StatusCounts = Record<StatusFilter, number>;
